import type {
  BotRegistryVendor,
  ConnectionFieldName,
  ConnectionFieldRule,
  ConnectionFieldRules,
} from "features/adminUsers/adminUsers";

/** Every connection form, after the vendor that decides the rules (CAL-6158). */
export const CONNECTION_FIELD_ORDER: ConnectionFieldName[] = [
  "host",
  "client_code",
  "username",
  "password",
];

export type ConnectionValues = Record<ConnectionFieldName, string>;

export type ConnectionFieldMessages = Partial<Record<ConnectionFieldName, string>>;

export const EMPTY_CONNECTION_VALUES: ConnectionValues = {
  host: "",
  client_code: "",
  username: "",
  password: "",
};

function plain(
  use: ConnectionFieldRule["use"],
  label: string,
  example = "",
): ConnectionFieldRule {
  return { use, label, hint: "", example, max_length: null, domains: [] };
}

/** The form a connection always had, for a vendor the rules do not cover. */
export function legacyConnectionRules(vendor: string): ConnectionFieldRules {
  return {
    host: plain("optional", "Host", `default ${vendor.trim() || "vendor"} host`),
    client_code: { ...plain("optional", "Client code"), max_length: 20 },
    username: { ...plain("required", "Username"), max_length: 100 },
    password: plain("optional", "Password"),
  };
}

export function connectionRulesFor(
  vendors: Record<string, BotRegistryVendor> | undefined,
  vendor: string,
): ConnectionFieldRules {
  const key = vendor.trim().toLowerCase();
  return vendors?.[key]?.connection_fields ?? legacyConnectionRules(vendor);
}

export function shownConnectionFields(rules: ConnectionFieldRules): ConnectionFieldName[] {
  return CONNECTION_FIELD_ORDER.filter((name) => rules[name].use !== "unused");
}

/** The required fields left blank, said the way the server says it. */
export function missingConnectionFields(
  rules: ConnectionFieldRules,
  values: ConnectionValues,
): ConnectionFieldMessages {
  const missing: ConnectionFieldMessages = {};
  for (const name of shownConnectionFields(rules)) {
    const value = name === "password" ? values[name] : values[name].trim();
    if (rules[name].use === "required" && !value) {
      missing[name] = `${rules[name].label} is required.`;
    }
  }
  return missing;
}

function hostnameOf(value: string): string {
  const text = value.trim();
  if (!text) return "";
  try {
    return new URL(text.includes("://") ? text : `https://${text}`).hostname.toLowerCase();
  } catch {
    return "";
  }
}

/** A host on a domain the vendor does not serve: allowed (white-label
 *  portals exist), but worth a second look before saving. */
export function hostDomainWarning(
  rules: ConnectionFieldRules,
  vendor: string,
  host: string,
): string | null {
  const domains = rules.host.domains;
  const hostname = hostnameOf(host);
  if (rules.host.use === "unused" || !domains.length || !hostname) return null;
  const known = domains.some((d) => hostname === d || hostname.endsWith(`.${d}`));
  return known
    ? null
    : `${hostname} is not a known ${vendor.trim().toLowerCase()} domain; the bot will open it as it is.`;
}

/** What a form sends: the vendor's shown fields only, so a hidden one keeps
 *  whatever is stored. Passwords are sent as typed. */
export function connectionBody(
  rules: ConnectionFieldRules,
  values: ConnectionValues,
): Partial<ConnectionValues> {
  const body: Partial<ConnectionValues> = {};
  for (const name of shownConnectionFields(rules)) {
    body[name] = name === "password" ? values[name] : values[name].trim();
  }
  return body;
}

/** The server's per-field refusal, `{errors: {field: [message]}}`, split into
 *  what belongs at a field and what does not. */
export function serverConnectionErrors(error: unknown): {
  fields: ConnectionFieldMessages;
  vendor: string | null;
  other: string | null;
} {
  const data = (error as { data?: unknown } | null)?.data as
    | { errors?: Record<string, unknown>; error?: unknown; detail?: unknown }
    | undefined;
  const first = (value: unknown): string | null => {
    if (Array.isArray(value)) return value.length ? String(value[0]) : null;
    if (value == null || value === "") return null;
    return String(value);
  };
  const fields: ConnectionFieldMessages = {};
  let vendor: string | null = null;
  const rest: string[] = [];
  for (const [key, value] of Object.entries(data?.errors ?? {})) {
    const message = first(value);
    if (!message) continue;
    if ((CONNECTION_FIELD_ORDER as string[]).includes(key)) {
      fields[key as ConnectionFieldName] = message;
    } else if (key === "vendor") {
      vendor = message;
    } else {
      rest.push(message);
    }
  }
  const other = rest[0] ?? first(data?.error) ?? first(data?.detail);
  return { fields, vendor, other };
}

/** One line for a table cell: every field a saved login breaks. */
export function connectionProblemSummary(
  problems: ConnectionFieldMessages | undefined,
): string | null {
  const lines = CONNECTION_FIELD_ORDER.map((name) => problems?.[name]).filter(Boolean);
  return lines.length ? lines.join(" ") : null;
}
